import * as leaflet from "leaflet";
import { TileCoordinates, TileRectangle } from "../runescape/coordinates";
import Graticule from "./defaultlayers/Graticule";
import Widget from "../ui/Widget";
import ContextMenu from "../../cluetrainer/ui/widgets/ContextMenu";
import TileHighlightLayer from "./defaultlayers/TileHighlightLayer";
import { GameMapContextMenuEvent, GameMapKeyboardEvent, GameMapMouseEvent, GameMapViewChangedEvent } from "./MapEvents";
import { GameMapControl } from "./GameMapControl";
import BaseTileLayer from "./defaultlayers/BaseTileLayer";
import FloorControl from "./defaultlayers/FloorControl";
import { Rectangle, Vector2 } from "../math";
import { observe } from "../reactive";
import { GameLayer } from "./GameLayer";
import { InteractionGuard } from "./interaction/InteractionLayer";
import jquery from "jquery";
import { C } from "../ui/constructors";
import { Constants } from "../../cluetrainer/constants";
import { LeafletUtils } from "./LeafletUtils";
var cls = C.cls;
export const red_marker = "/assets/icons/marker_red.png";
export const blue_marker = "/assets/icons/marker_blue.png";
export const green_marker = "/assets/icons/marker_green.png";
export const yellow_marker = "/assets/icons/marker_yellow.png";
export function levelIcon(floor, scale = 1) {
    const levels = [
        "/assets/icons/marker_red.png",
        "/assets/icons/marker_blue.png",
        "/assets/icons/marker_green.png",
        "/assets/icons/marker_yellow.png",
    ];
    return leaflet.icon({
        iconUrl: levels[floor],
        iconSize: [scale * 18, scale * 24],
        iconAnchor: [scale * 9, scale * 24],
        className: "marker-icon",
    });
}
/**
 * This map class wraps a leaflet map view and provides features needed for the solver.
 * Map data is sourced from Skillbert's amazing runeapps.org.
 */
export class GameMap extends leaflet.Map {
    constructor(element) {
        super(element, GameMap.gameMapOptions());
        this.floor = observe(0);
        this.viewport = observe(null).equality(GameMap.View.equals);
        this._lastHoveredTile = null;
        this.fitArea = null;
        this.debug_show_fit_area = false;
        this.container = jquery(element);
        {
            // Setup custom panes for z layering
            const overlay_pane = this.getPane("overlayPane");
            const areaPane = this.createPane(GameMap.areaPane, overlay_pane);
            const pathTargetPane = this.createPane(GameMap.pathTargetPane, overlay_pane);
            const objectPane = this.createPane(GameMap.objectPane, overlay_pane);
            const pathArrowPane = this.createPane(GameMap.pathArrowPane, overlay_pane);
            areaPane.style.zIndex = "410";
            pathTargetPane.style.zIndex = "420";
            objectPane.style.zIndex = "430";
            pathArrowPane.style.zIndex = "440";
        }
        // Set up UI layers
        {
            this.ui_container = cls("gamemap-ui-container").appendTo(this.container);
            //this.top_control_container.container.on("click", (e) => e.stopPropagation())
            // TODO: prevent event propagation like above?
            for (let key in GameMap.position_layer_class_mapping) {
                cls(GameMap.position_layer_class_mapping[key])
                    .appendTo(this.ui_container);
            }
        }
        const gridPane = this.createPane("gridPane");
        gridPane.style.zIndex = "250"; // Above tile pane, below overlay pane
        this.internal_root_layer = new GameLayer();
        this.addLayer(this.internal_root_layer);
        this.addGameLayer(new FloorControl())
            .addGameLayer(new TileHighlightLayer());
        // Set up all the event handlers to translate into GameMapEvents
        {
            this.on("contextmenu", async (e) => {
                e.originalEvent.preventDefault();
                let event = this.event(new GameMapContextMenuEvent(this, e, this.eventCoordinate(e)), (l) => (e) => l.eventContextMenu(e));
                if (event.active_entity) {
                    await this.internal_root_layer.lockEntity(event.active_entity);
                }
                new ContextMenu(await event.getMenu())
                    .show(this.container.get()[0], { x: e.originalEvent.clientX, y: e.originalEvent.clientY })
                    .onClosed(async () => {
                    this.container.focus();
                    await this.internal_root_layer.lockEntity(null);
                });
            });
            this.on("click", (e) => {
                e.originalEvent.preventDefault();
                this.event(new GameMapMouseEvent(this, e, this.eventCoordinate(e)), (l) => (e) => l.eventClick(e));
            });
            this.on("mousemove", (e) => {
                let t = TileCoordinates.snap(this.eventCoordinate(e));
                if (this.internal_root_layer.activeEntity()?.isStillHovered() == false) {
                    this.internal_root_layer.requestEntityActivation(null);
                }
                if (!TileCoordinates.eq2(t, this._lastHoveredTile)) {
                    this._lastHoveredTile = t;
                    this.event(new GameMapMouseEvent(this, e, t), (l) => (e) => l.eventHover(e));
                }
            });
            this.on("mouseup", (e) => {
                e.originalEvent.preventDefault();
                this.event(new GameMapMouseEvent(this, e, this.eventCoordinate(e)), (l) => (e) => l.eventMouseUp(e));
            });
            this.on("mousedown", (e) => {
                e.originalEvent.preventDefault();
                e.originalEvent.stopPropagation();
                this.event(new GameMapMouseEvent(this, e, this.eventCoordinate(e)), (l) => (e) => l.eventMouseDown(e));
            });
            this.on("keydown", (e) => {
                this.event(new GameMapKeyboardEvent(this, e), l => e => l.eventKeyDown(e));
            });
            this.on("keyup", (e) => {
                this.event(new GameMapKeyboardEvent(this, e), l => e => l.eventKeyUp(e));
            });
            this.viewport.subscribe((new_value, old) => {
                this.event(new GameMapViewChangedEvent(this, old, new_value), l => e => l.eventViewChanged(e));
            });
            this.on("moveend", () => this.updateView());
            this.on("zoomend", () => this.updateView());
            this.floor.subscribe(() => this.updateView());
        }
        // Add subtle gridlines
        new Graticule({
            intervals: [
                { min_zoom: -3, interval: 64 },
                { min_zoom: 0.5, interval: 8 },
                { min_zoom: 1, interval: 4 },
                { min_zoom: 2, interval: 1 },
            ],
            lineStyle: {
                weight: 1,
                color: '#111111',
                opacity: 0.25,
                interactive: false
            },
            pane: "gridPane"
        })
            .addTo(this);
        this.updateBaseLayers();
        this.floor.subscribe(() => this.updateBaseLayers());
    }
    updateView() {
        const bounds = this.getBounds();
        this.viewport.set({
            rect: TileRectangle.lift(Rectangle.extend(Rectangle.from(LeafletUtils.latLongToVector2(bounds.getNorthEast()), LeafletUtils.latLongToVector2(bounds.getSouthWest())), 1), this.floor.value()),
            zoom: this.getZoom()
        });
        return this;
    }
    fitView(view, options = {}) {
        options.maxZoom ?? (options.maxZoom = 4);
        this.invalidateSize();
        const finalbounds = LeafletUtils.convert_bounds(LeafletUtils.boundsFromRectangle(view)).pad(0.1);
        this.fitBounds(finalbounds, options);
        if (this.debug_show_fit_area) {
            this.fitArea?.remove();
            this.fitArea = leaflet.polygon([finalbounds.getNorthEast(), finalbounds.getNorthWest(), finalbounds.getSouthWest(), finalbounds.getSouthEast()]).setStyle({
                color: "#FFFFFF",
                weight: 5
            }).addTo(this);
        }
        this.floor.set(view.level);
        return this;
    }
    getClientPos(coordinates) {
        return Vector2.add(this.latLngToContainerPoint(LeafletUtils.latLongFromVector2(coordinates)), {
            x: this.container.get()[0].getBoundingClientRect().left,
            y: this.container.get()[0].getBoundingClientRect().top
        });
    }
    addGameLayer(layer) {
        this.internal_root_layer.add(layer);
        return this;
    }
    addControl(control) {
        if (!(control instanceof GameMapControl)) {
            super.addControl(control);
        }
        else {
            let self = this;
            function getPositionLayer(position) {
                return self.ui_container.container.children(`.${GameMap.position_layer_class_mapping[position]}`);
            }
            control.content.appendTo(getPositionLayer(control.config.position));
        }
        return this;
    }
    updateBaseLayers() {
        const MAP_ID = 4; // Hardcoded
        const SKILLBERT_ATTRIBUTION = '<a href="https://runeapps.org/" title="Creator of Alt1 and RuneApps.org">Skillbert</a>';
        function geturls(filename) {
            return [
                `https://runeapps.org/s3/map${MAP_ID}/live/${filename}`,
                `https://runeapps.org/node/map/getnamed?mapid=${MAP_ID}&version=${Constants.map_version}&file=${filename}`
            ];
        }
        const layers = [
            // Rendered Top Down Layer
            new BaseTileLayer([
                { urls: geturls(`topdown-${this.floor.value()}/{z}/{x}-{y}.webp`) }
            ], {
                attribution: SKILLBERT_ATTRIBUTION,
                tileSize: 512,
                maxNativeZoom: 5,
                minZoom: -5,
                bounds: GameMap.bounds()
            }),
            // Walls SVG Layer
            new BaseTileLayer([
                { to: 2, urls: geturls(`walls-${this.floor.value()}/{z}/{x}-{y}.webp`) },
                { from: 3, to: 3, urls: geturls(`walls-${this.floor.value()}/{z}/{x}-{y}.svg`) }
            ], {
                attribution: SKILLBERT_ATTRIBUTION,
                tileSize: 512,
                maxNativeZoom: 3,
                pane: GameMap.objectPane,
                minZoom: -5,
                bounds: GameMap.bounds()
            }),
            // Filtered Collision Layer
            new BaseTileLayer([
                { urls: geturls(`collision-${this.floor.value()}/{z}/{x}-{y}.png`) }
            ], {
                attribution: SKILLBERT_ATTRIBUTION,
                tileSize: 512,
                maxNativeZoom: 3,
                minZoom: -5,
                className: "map-collisionlayer",
                bounds: GameMap.bounds()
            })
        ];
        const oldbase = this.baseLayers;
        if (oldbase && oldbase.length > 0) {
            //prevent loading of new tiles on old layer
            oldbase.forEach(q => q.on("tileloadstart", e => e.target.src = ""));
            layers[0].on("load", () => oldbase.forEach(q => q.remove()));
            // Definitely remove old base layers after 500 ms in case the event doesn't trigger
            setTimeout(() => oldbase.forEach(q => q.remove()), 500);
        }
        this.baseLayers = layers;
        layers.forEach((l) => l.addTo(this));
    }
    eventCoordinate(e) {
        return {
            x: e.latlng.lng,
            y: e.latlng.lat,
            level: this.floor.value()
        };
    }
    event(event, h) {
        function propagate(l) {
            if (!l)
                return;
            event.propagation_state.phase = "pre";
            h(l)(event);
            if (!event.propagation_state.trickle_stopped && !event.propagation_state.trickle_stopped_immediate) {
                l.eachLayer(lay => {
                    if (lay instanceof GameLayer)
                        propagate(lay);
                });
            }
            if (event.propagation_state.trigger_post_order) {
                event.propagation_state.phase = "post";
                h(l)(event);
            }
            event.propagation_state.trickle_stopped_immediate = false;
        }
        event.active_entity = this.internal_root_layer.getHoveredEntity();
        if (!event.active_entity?.isStillHovered())
            event.active_entity = null;
        propagate(this.internal_root_layer);
        return event;
    }
}
(function (GameMap) {
    GameMap.areaPane = "underlayAreaPane";
    GameMap.pathArrowPane = "pathArrowPane";
    GameMap.pathTargetPane = "pathAreaPane";
    GameMap.objectPane = "objectAreaPane";
    GameMap.size = {
        chunks: { x: 100, y: 200 },
        chunk_size: { x: 64, y: 64 },
    };
    function bounds() {
        return new leaflet.LatLngBounds([0, 0], [GameMap.size.chunks.y * GameMap.size.chunk_size.y, GameMap.size.chunks.x * GameMap.size.chunk_size.x]);
    }
    GameMap.bounds = bounds;
    function gameMapOptions() {
        function getCRS() {
            const chunkoffset = {
                x: 16,
                z: 16
            };
            const mapsize = {
                x: 100,
                z: 200
            };
            const chunksize = 64;
            let crs = leaflet.CRS.Simple;
            //add 0.5 to so coords are center of tile
            // @ts-ignore
            crs.transformation = leaflet.transformation(1, chunkoffset.x + 0.5, -1, mapsize.z * chunksize + -1 * (chunkoffset.z + 0.5));
            return crs;
        }
        return {
            crs: getCRS(),
            zoomSnap: 0.5,
            zoomDelta: 0.5,
            minZoom: -5,
            maxZoom: 7,
            zoomControl: false,
            dragging: true,
            doubleClickZoom: false,
            boxZoom: false,
            attributionControl: false,
            preferCanvas: false,
            maxBounds: bounds(),
            maxBoundsViscosity: 0.5
        };
    }
    GameMap.gameMapOptions = gameMapOptions;
    GameMap.position_layer_class_mapping = {
        "bottom-center": "gamemap-ui-layer-bc",
        "bottom-left": "gamemap-ui-layer-bl",
        "bottom-right": "gamemap-ui-layer-br",
        "left-bottom": "gamemap-ui-layer-lb",
        "left-center": "gamemap-ui-layer-lc",
        "left-top": "gamemap-ui-layer-lt",
        "right-bottom": "gamemap-ui-layer-rb",
        "right-center": "gamemap-ui-layer-rc",
        "right-top": "gamemap-ui-layer-rt",
        "top-center": "gamemap-ui-layer-tc",
        "top-left": "gamemap-ui-layer-tl",
        "top-right": "gamemap-ui-layer-tr"
    };
    let View;
    (function (View) {
        function equals(a, b) {
            return TileRectangle.equals(a?.rect, b?.rect) && a?.zoom == b?.zoom;
        }
        View.equals = equals;
    })(View = GameMap.View || (GameMap.View = {}));
})(GameMap || (GameMap = {}));
export class GameMapWidget extends Widget {
    constructor(container = jquery("<div>")) {
        super(container);
        this.map = new GameMap(container.get()[0])
            .setView([3200, 3000], 0);
    }
}
export class GameMapMiniWidget extends Widget {
    constructor() {
        super();
        this.map = new GameMap(this.raw())
            .setView([3200, 3000], 0);
        this.main_layer = new GameLayer().addTo(this.map);
        this.interaction_guard = new InteractionGuard().setDefaultLayer(this.main_layer);
    }
    setInteraction(interaction) {
        this.interaction_guard.set(interaction);
        return this;
    }
}
